import React, { useState, useEffect, useRef } from 'react'
import { Heart, MessageCircle, Share, Bookmark, MoreHorizontal, Plus, Search, Bell, Home, Users, Camera, User, Send, X, Upload, Eye, ChevronLeft, ChevronRight, Brain, Trophy, ShoppingCart, BarChart3, Activity, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import SocialWorld from '../../App/World/SocialWorld'
import useUserData from '../../Hooks/useUserData'
import { database } from '../../Config/firebase'
import { ref, push, set, onValue, off, remove, query, orderByChild, get, update } from '../../Config/matuRtdb'

export default function HomePages() {
  const { user, loading } = useUserData()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [stories, setStories] = useState([])
  const [showStoryModal, setShowStoryModal] = useState(false)
  const [storyText, setStoryText] = useState('')
  const [storyImage, setStoryImage] = useState('')
  const [storyImageFile, setStoryImageFile] = useState(null)
  const [viewingStory, setViewingStory] = useState(null)
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0)
  const [currentUserId, setCurrentUserId] = useState(null)
  const [currentUserData, setCurrentUserData] = useState(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [storyProgress, setStoryProgress] = useState(0)

  // Obtener UID desde localStorage
  useEffect(() => {
    const storedUid = localStorage.getItem('userUID')
    if (storedUid) {
      setCurrentUserId(storedUid)
    }
  }, [])

  // Obtener datos del usuario actual desde Firebase
  useEffect(() => {
    if (!currentUserId) return

    const userRef = ref(database, `usuarios/${currentUserId}`)
    const unsubscribe = onValue(userRef, (snapshot) => {
      const userData = snapshot.val()
      if (userData) {
        setCurrentUserData(userData)
      }
    })

    return () => off(userRef, 'value', unsubscribe)
  }, [currentUserId])

  // Cargar historias desde Firebase
  useEffect(() => {
    if (!currentUserId) return

    const storiesRef = ref(database, 'world/stories')
    const storiesQuery = query(storiesRef, orderByChild('timestamp'))

    const unsubscribe = onValue(storiesQuery, (snapshot) => {
      const storiesData = snapshot.val()
      const now = Date.now()
      const validStories = []
      const expiredStories = []

      if (storiesData) {
        Object.entries(storiesData).forEach(([key, story]) => {
          const storyAge = now - story.timestamp
          const twentyFourHours = 24 * 60 * 60 * 1000

          if (storyAge > twentyFourHours) {
            expiredStories.push(key)
          } else {
            validStories.push({ id: key, ...story })
          }
        })

        expiredStories.forEach(storyId => {
          remove(ref(database, `world/stories/${storyId}`))
        })
      }

      const groupedStories = validStories.reduce((acc, story) => {
        if (!acc[story.userId]) {
          acc[story.userId] = {
            userId: story.userId,
            userName: story.userName,
            userAvatar: story.userAvatar,
            stories: []
          }
        }
        acc[story.userId].stories.push(story)
        return acc
      }, {})

      const storiesArray = []
      
      const userStories = groupedStories[currentUserId]?.stories || []
      storiesArray.push({
        userId: currentUserId,
        userName: currentUserData?.name || 'Tu Historia',
        userAvatar: currentUserData?.photoURL || currentUserData?.avatar || 'https://avatar.iran.liara.run/public/',
        stories: userStories,
        isOwn: true
      })

      Object.values(groupedStories).forEach(storyGroup => {
        if (storyGroup.userId !== currentUserId) {
          storiesArray.push({
            ...storyGroup,
            isOwn: false
          })
        }
      })

      setStories(storiesArray)
    })

    return () => off(storiesRef, 'value', unsubscribe)
  }, [currentUserId, currentUserData])

  // Progreso automático de historia
  useEffect(() => {
    let interval
    if (viewingStory && viewingStory.stories.length > 0) {
      interval = setInterval(() => {
        setStoryProgress(prev => {
          if (prev >= 100) {
            nextStory()
            return 0
          }
          return prev + 2
        })
      }, 100)
    }
    return () => clearInterval(interval)
  }, [viewingStory, currentStoryIndex])

  // Convertir imagen a base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
    })
  }

  // Manejar selección de imagen
  const handleImageSelect = async (event) => {
    const file = event.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen debe ser menor a 5MB')
        return
      }
      
      setUploadingImage(true)
      try {
        const base64 = await convertToBase64(file)
        setStoryImage(base64)
        setStoryImageFile(file)
      } catch (error) {
        console.error('Error converting image:', error)
        alert('Error al procesar la imagen')
      } finally {
        setUploadingImage(false)
      }
    }
  }

  // Crear nueva historia
  const createStory = async () => {
    if (!currentUserId || (!storyText.trim() && !storyImage) || !currentUserData) return

    try {
      const storiesRef = ref(database, 'world/stories')
      const newStoryRef = push(storiesRef)
      
      await set(newStoryRef, {
        userId: currentUserId,
        userName: currentUserData.name || 'Usuario',
        userAvatar: currentUserData.photoURL || currentUserData.avatar || 'https://avatar.iran.liara.run/public/',
        text: storyText.trim(),
        image: storyImage,
        timestamp: Date.now(),
        views: {},
        likes: {}
      })

      setStoryText('')
      setStoryImage('')
      setStoryImageFile(null)
      setShowStoryModal(false)
    } catch (error) {
      console.error('Error creating story:', error)
      alert('Error al crear la historia. Inténtalo de nuevo.')
    }
  }

  // Marcar historia como vista
  const markStoryAsViewed = async (storyId) => {
    if (!currentUserId || !storyId || !currentUserData) return
    
    try {
      const storyRef = ref(database, `world/stories/${storyId}/views/${currentUserId}`)
      await set(storyRef, {
        timestamp: Date.now(),
        userName: currentUserData.name || 'Usuario'
      })
    } catch (error) {
      console.error('Error marking story as viewed:', error)
    }
  }

  // Dar like a historia
  const likeStory = async (storyId) => {
    if (!currentUserId || !storyId || !currentUserData) return
    
    try {
      const storyLikeRef = ref(database, `world/stories/${storyId}/likes/${currentUserId}`)
      const snapshot = await get(storyLikeRef)
      
      if (snapshot.exists()) {
        await remove(storyLikeRef)
      } else {
        await set(storyLikeRef, {
          timestamp: Date.now(),
          userName: currentUserData.name || 'Usuario'
        })
      }
    } catch (error) {
      console.error('Error liking story:', error)
    }
  }

  // Funciones de navegación
  const handleNavigation = (path) => {
    navigate(path)
  }

  // Ver historia
  const viewStory = (storyGroup, index = 0) => {
    if (storyGroup.stories.length > 0) {
      setViewingStory(storyGroup)
      setCurrentStoryIndex(index)
      setStoryProgress(0)
      
      // Marcar como vista si no es propia
      if (!storyGroup.isOwn) {
        markStoryAsViewed(storyGroup.stories[index].id)
      }
    } else if (storyGroup.isOwn) {
      setShowStoryModal(true)
    }
  }

  // Navegar entre historias
  const nextStory = () => {
    if (!viewingStory) return
    
    if (currentStoryIndex < viewingStory.stories.length - 1) {
      const newIndex = currentStoryIndex + 1
      setCurrentStoryIndex(newIndex)
      setStoryProgress(0)
      
      if (!viewingStory.isOwn) {
        markStoryAsViewed(viewingStory.stories[newIndex].id)
      }
    } else {
      setViewingStory(null)
      setCurrentStoryIndex(0)
      setStoryProgress(0)
    }
  }

  const prevStory = () => {
    if (viewingStory && currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1)
      setStoryProgress(0)
    }
  }

  // Cerrar visualizador de historia
  const closeStoryViewer = () => {
    setViewingStory(null)
    setCurrentStoryIndex(0)
    setStoryProgress(0)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  if (!currentUserId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
          <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Inicia Sesión</h2>
          <p className="text-gray-600 mb-6">Debes iniciar sesión para ver las historias</p>
          <button 
            onClick={() => navigate('/gta-app/login')}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg"
          >
            Iniciar Sesión
          </button>
        </div>
      </div>
    )
  }

  const currentStory = viewingStory?.stories[currentStoryIndex]
  const currentStoryLikes = currentStory?.likes ? Object.keys(currentStory.likes).length : 0
  const currentStoryViews = currentStory?.views ? Object.keys(currentStory.views).length : 0
  const isLikedByMe = currentStory?.likes?.[currentUserId] ? true : false

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-purple-200/50 sticky top-0 z-40 shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-xl px-4 py-2 rounded-xl shadow-lg">
              Social
            </div>
            <div className="hidden md:flex relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type="text"
                placeholder="Buscar historias, usuarios..."
                className="pl-10 pr-4 py-2 bg-gray-100/80 rounded-full w-64 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white transition-all backdrop-blur-sm"
              />
            </div>
          </div>
          
          <nav className="flex items-center space-x-6">
            <Home 
              className="w-6 h-6 text-purple-600 cursor-pointer hover:scale-110 transition-transform" 
              onClick={() => handleNavigation('/gta-app/home')}
            />
            <Users 
              className="w-6 h-6 text-gray-600 cursor-pointer hover:text-purple-600 hover:scale-110 transition-all"
              onClick={() => handleNavigation('/gta-app/home')}
            />
            <Camera 
              className="w-6 h-6 text-gray-600 cursor-pointer hover:text-purple-600 hover:scale-110 transition-all"
              onClick={() => handleNavigation('/gta-app/home')}
            />
            <Bell 
              className="w-6 h-6 text-gray-600 cursor-pointer hover:text-purple-600 hover:scale-110 transition-all"
              onClick={() => handleNavigation('/gta-app/home')}
            />
            <div className="relative group">
              <button className="w-6 h-6 text-gray-600 hover:text-purple-600 hover:scale-110 transition-all">
                <MoreHorizontal className="w-6 h-6" />
              </button>
              <div className="absolute right-0 top-8 w-64 bg-white/95 backdrop-blur-xl rounded-xl shadow-xl border border-purple-200/50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <div className="p-2 space-y-1">
                  <button 
                    onClick={() => handleNavigation('/features/ai-predictions')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <Brain className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium">IA Predictions</span>
                  </button>
                  <button 
                    onClick={() => handleNavigation('/features/tournaments')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <Trophy className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm font-medium">Torneos</span>
                  </button>
                  <button 
                    onClick={() => handleNavigation('/features/marketplace')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <ShoppingCart className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium">Marketplace</span>
                  </button>
                  <button 
                    onClick={() => handleNavigation('/features/analytics')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <BarChart3 className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">Analytics</span>
                  </button>
                  <button 
                    onClick={() => handleNavigation('/features/live-streaming')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <Activity className="w-4 h-4 text-red-600" />
                    <span className="text-sm font-medium">Live Streaming</span>
                  </button>
                  <button 
                    onClick={() => handleNavigation('/features/gamification')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <Zap className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium">Gamificación</span>
                  </button>
                </div>
              </div>
            </div>
            <div 
              className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full cursor-pointer hover:scale-110 transition-transform shadow-md"
              onClick={() => handleNavigation('/gta-app/home')}
            >
              {currentUserData?.photoURL || currentUserData?.avatar ? (
                <img 
                  src={currentUserData.photoURL || currentUserData.avatar} 
                  alt="Profile" 
                  className="w-full h-full rounded-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none'
                  }}
                />
              ) : null}
            </div>
          </nav>
        </div>
      </header>

      <div className="px-2 py-1 max-w-4xl mx-auto">
        {/* Stories Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 mb-4 shadow-xl border border-white/50">
          <div className="flex space-x-4 overflow-x-auto pb-2 scrollbar-hide">
            {stories.map((storyGroup) => (
              <div key={storyGroup.userId} className="flex flex-col items-center space-y-2 flex-shrink-0">
                <div 
                  className={`relative cursor-pointer group ${
                    storyGroup.isOwn 
                      ? 'ring-3 ring-blue-500' 
                      : storyGroup.stories.length > 0 
                        ? 'ring-3 ring-gradient-to-r from-purple-500 to-pink-500' 
                        : 'ring-2 ring-gray-300'
                  } rounded-full p-1 transition-all hover:scale-105`}
                  onClick={() => viewStory(storyGroup)}
                >
                  <img 
                    src={storyGroup.userAvatar} 
                    alt={storyGroup.userName}
                    className="w-16 h-16 rounded-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://avatar.iran.liara.run/public/'
                    }}
                  />
                  {storyGroup.isOwn && (
                    <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg">
                      <Plus className="w-4 h-4" />
                    </div>
                  )}
                  
                  {storyGroup.stories.length > 0 && (
                    <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                      {storyGroup.stories.length}
                    </div>
                  )}
                </div>
                <span className="text-sm text-gray-700 text-center max-w-20 truncate font-medium">
                  {storyGroup.isOwn ? 'Tu Historia' : storyGroup.userName}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Social World */}
        <SocialWorld/>
        
        {/* Nuevas funcionalidades */}
        <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-green-600 rounded-xl p-6 mb-4 shadow-xl">
          <div className="text-center mb-4">
            <h2 className="text-2xl font-bold text-white mb-2">🚀 Nuevas Funcionalidades</h2>
            <p className="text-white/80">Descubre todas las características premium que hemos agregado</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div 
              className="bg-white/20 backdrop-blur-sm rounded-lg p-4 cursor-pointer hover:scale-105 transition-all"
              onClick={() => navigate('/features/ai-predictions')}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">IA Predictions</h3>
                  <p className="text-white/80 text-sm">Análisis inteligente</p>
                </div>
              </div>
            </div>
            
            <div 
              className="bg-white/20 backdrop-blur-sm rounded-lg p-4 cursor-pointer hover:scale-105 transition-all"
              onClick={() => navigate('/features/tournaments')}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Torneos</h3>
                  <p className="text-white/80 text-sm">Compite y gana</p>
                </div>
              </div>
            </div>
            
            <div 
              className="bg-white/20 backdrop-blur-sm rounded-lg p-4 cursor-pointer hover:scale-105 transition-all"
              onClick={() => navigate('/features/marketplace')}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Marketplace</h3>
                  <p className="text-white/80 text-sm">Compra predicciones</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <button 
              onClick={() => navigate('/features')}
              className="bg-white text-purple-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-all shadow-lg"
            >
              Ver Todas las Funcionalidades
            </button>
          </div>
        </div>
      </div>

      {/* Modal para crear historia */}
      {showStoryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl transform animate-in slide-in-from-bottom-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">Crear Historia</h3>
                <button 
                  onClick={() => {
                    setShowStoryModal(false)
                    setStoryText('')
                    setStoryImage('')
                    setStoryImageFile(null)
                  }}
                  className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {storyImage && (
                  <div className="relative">
                    <img 
                      src={storyImage} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-xl"
                    />
                    <button
                      onClick={() => {
                        setStoryImage('')
                        setStoryImageFile(null)
                      }}
                      className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70 transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                <textarea
                  value={storyText}
                  onChange={(e) => setStoryText(e.target.value)}
                  placeholder="¿Qué quieres compartir hoy?"
                  className="w-full p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  rows="4"
                  maxLength="200"
                />
                
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="flex items-center space-x-2 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-xl transition-all disabled:opacity-50"
                  >
                    {uploadingImage ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                    ) : (
                      <Upload className="w-5 h-5" />
                    )}
                    <span>Subir Imagen</span>
                  </button>
                  
                  <span className="text-sm text-gray-500">
                    {storyText.length}/200
                  </span>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowStoryModal(false)
                      setStoryText('')
                      setStoryImage('')
                      setStoryImageFile(null)
                    }}
                    className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={createStory}
                    disabled={!storyText.trim() && !storyImage}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg"
                  >
                    Publicar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para ver historia */}
      {viewingStory && viewingStory.stories.length > 0 && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <div className="relative w-full h-full max-w-md mx-auto bg-black">
            {/* Barras de progreso */}
            <div className="absolute top-4 left-4 right-4 flex space-x-1 z-20">
              {viewingStory.stories.map((_, index) => (
                <div key={index} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                  <div 
                    className={`h-full bg-white transition-all duration-300 ${
                      index < currentStoryIndex ? 'w-full' : 
                      index === currentStoryIndex ? `w-[${storyProgress}%]` : 'w-0'
                    }`}
                    style={index === currentStoryIndex ? { width: `${storyProgress}%` } : {}}
                  />
                </div>
              ))}
            </div>

            {/* Header de la historia */}
            <div className="absolute top-8 left-4 right-4 flex items-center justify-between text-white z-20">
              <div className="flex items-center space-x-3">
                <img 
                  src={viewingStory.userAvatar} 
                  alt={viewingStory.userName}
                  className="w-10 h-10 rounded-full border-2 border-white/50"
                />
                <div>
                  <span className="font-semibold">{viewingStory.userName}</span>
                  <div className="flex items-center space-x-3 text-sm text-white/80">
                    <span className="flex items-center space-x-1">
                      <Eye className="w-4 h-4" />
                      <span>{currentStoryViews}</span>
                    </span>
                    {currentStoryLikes > 0 && (
                      <span className="flex items-center space-x-1">
                        <Heart className="w-4 h-4 text-red-400" />
                        <span>{currentStoryLikes}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button 
                onClick={closeStoryViewer}
                className="text-white hover:text-gray-300 p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Contenido de la historia */}
            <div className="w-full h-full flex items-center justify-center relative">
              <div className="text-center text-white p-8 max-w-sm">
                {currentStory?.image && (
                  <img 
                    src={currentStory.image}
                    alt="Story"
                    className="max-w-full max-h-96 mx-auto mb-4 rounded-xl shadow-lg"
                  />
                )}
                {currentStory?.text && (
                  <p className="text-lg leading-relaxed font-medium">
                    {currentStory.text}
                  </p>
                )}
              </div>
            </div>

            {/* Controles de navegación */}
            <button
              onClick={prevStory}
              className="absolute left-0 top-0 w-1/3 h-full z-10 flex items-center justify-start pl-4"
            >
              {currentStoryIndex > 0 && (
                <ChevronLeft className="w-8 h-8 text-white/50 hover:text-white transition-colors" />
              )}
            </button>
            
            <button
              onClick={nextStory}
              className="absolute right-0 top-0 w-2/3 h-full z-10 flex items-center justify-end pr-4"
            >
              {currentStoryIndex < viewingStory.stories.length - 1 && (
                <ChevronRight className="w-8 h-8 text-white/50 hover:text-white transition-colors" />
              )}
            </button>

            {/* Botón de like para historias de otros usuarios */}
            {!viewingStory.isOwn && (
              <button
                onClick={() => likeStory(currentStory.id)}
                className="absolute bottom-8 right-4 z-20 bg-black/50 backdrop-blur-sm rounded-full p-3 hover:bg-black/70 transition-all"
              >
                <Heart 
                  className={`w-6 h-6 ${
                    isLikedByMe ? 'text-red-400 fill-red-400' : 'text-white'
                  } transition-colors`}
                />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}